import TokenRepositoryInterface from './repositories/interfaces/TokenRepositoryInterface';
import UserRepositoryInterface from './repositories/interfaces/UserRepositoryInterface';
import PasswordRepositoryInterface from './repositories/interfaces/PasswordRepositoryInterface';
import TranslatorRepositoryInterface from './repositories/interfaces/TranslatorRepositoryInterface';
import ConnectionRepositoryInterface from './repositories/interfaces/ConnectionRepositoryInterface';
import ClientRepositoryInterface from './repositories/interfaces/ClientRepositoryInterface';
import GroupRepositoryInterface from './repositories/interfaces/GroupRepositoryInterface';
import DeviceRepositoryInterface from './repositories/interfaces/DeviceRepositoryInterface';
import DeviceTypeRepositoryInterface from './repositories/interfaces/DeviceTypeRepositoryInterface';
import MonitorRepositoryInterface from './repositories/interfaces/MonitorRepositoryInterface';
import MetricRepositoryInterface from './repositories/interfaces/MetricRepositoryInterface';

/**
 * Binds the given factory to the given interface.
 *
 * @param {function} repositoryFactory The factory method to initialize the given repository.
 * @param {object} Interface The interface of methods for the given repository.
 */
function bind (repositoryFactory, Interface) {
	return {
		...Object.keys(Interface).reduce((prev, method) => {
			const resolveableMethod = async (...args) => {
				const repository = await repositoryFactory();
				return repository.default[method](...args);
			};

			return { ...prev, [method]: resolveableMethod };
		}, {})
	};
}

export default {
	/**
	 * Loads the tokenRepository when it's injected in a component.
	 *
	 * @returns {object}
	 */
	get tokenRepository () {
		const tokenRepositoryPromise = import('./repositories/TokenRepository');
		return bind(() => tokenRepositoryPromise, TokenRepositoryInterface);
	},

	/**
	 * Loads the userRepository when a method of that repository is called after it's
	 * injected into a component.
	 *
	 * @returns {object}
	 */
	get userRepository () {
		// Delay loading until a method of the repository is called.
		return bind(() => import('./repositories/UserRepository'), UserRepositoryInterface);
	},

	/**
	 * Loads the passwordRepository when a method of that repository is called after it's
	 * injected into a component.
	 *
	 * @returns {object}
	 */
	get passwordRepository () {
		return bind(() => import('./repositories/PasswordRepository'), PasswordRepositoryInterface);
	},

	/**
	 * Loads the translatorRepository when a method of that repository is called after it's
	 * injected into a component.
	 *
	 * @returns {object}
	 */
	get translatorRepository () {
		return bind(() => import('./repositories/TranslatorRepository'), TranslatorRepositoryInterface);
	},

	/**
	 * Loads the connectionRepository when a method of that repository is called after it's
	 * injected into a component.
	 *
	 * @returns {object}
	 */
	get connectionRepository () {
		return bind(() => import('./repositories/ConnectionRepository'), ConnectionRepositoryInterface);
	},

	/**
	 * Loads the clientRepository when a method of that repository is called after it's
	 * injected into a component.
	 *
	 * @returns {object}
	 */
	get clientRepository () {
		return bind(() => import('./repositories/ClientRepository'), ClientRepositoryInterface);
	},

	/**
	 * Loads the groupRepository when a method of that repository is called after it's
	 * injected into a component.
	 *
	 * @returns {object}
	 */
	get groupRepository () {
		return bind(() => import('./repositories/GroupRepository'), GroupRepositoryInterface);
	},

	/**
	 * Loads the deviceRepository when a method of that repository is called after it's
	 * injected into a component.
	 *
	 * @returns {object}
	 */
	get deviceRepository () {
		return bind(() => import('./repositories/DeviceRepository'), DeviceRepositoryInterface);
	},

	/**
	 * Loads the deviceRepository when a method of that repository is called after it's
	 * injected into a component.
	 *
	 * @returns {object}
	 */
	get deviceTypeRepository () {
		return bind(() => import('./repositories/DeviceTypeRepository'), DeviceTypeRepositoryInterface);
	},

	/**
	 * Loads the monitorRepository when a method of that repository is called after it's
	 * injected into a component.
	 *
	 * @returns {object}
	 */
	get monitorRepository () {
		return bind(() => import('./repositories/MonitorRepository'), MonitorRepositoryInterface);
	},

	/**
	 * Loads the metricRepository when a method of that repository is called after it's
	 * injected into a component.
	 *
	 * @returns {object}
	 */
	get metricRepository () {
		return bind(() => import('./repositories/MetricRepository'), MetricRepositoryInterface);
	}
};
